// Card.tsx — Container surface bound to design tokens, with three-slot composition.
import React, { useState } from 'react';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
    interactive?: boolean;
}

const SLOT = Symbol('CardSlot');

type SlotKind = 'header' | 'body' | 'footer';

interface SlotComponent<P = React.HTMLAttributes<HTMLDivElement>> extends React.FC<P> {
    [SLOT]?: SlotKind;
}

const isSlot = (child: React.ReactNode, kind: SlotKind): boolean => {
    if (!React.isValidElement(child)) return false;
    const t = child.type as { [SLOT]?: SlotKind } | undefined;
    return !!t && t[SLOT] === kind;
};

const hasAnySlot = (children: React.ReactNode): boolean => {
    let found = false;
    React.Children.forEach(children, (c) => {
        if (isSlot(c, 'header') || isSlot(c, 'body') || isSlot(c, 'footer')) found = true;
    });
    return found;
};

const headerStyle: React.CSSProperties = {
    padding: '0.5rem 1rem',
    borderBottom: '1px solid var(--color-border-subtle)',
    font: '700 var(--type-title-size)/var(--type-title-line-height) var(--font-mono)',
    color: 'var(--color-text-primary)',
};

const bodyStyle: React.CSSProperties = {
    padding: '1rem 1.25rem',
};

const footerStyle: React.CSSProperties = {
    padding: '0.5rem 1rem',
    borderTop: '1px solid var(--color-border-subtle)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-sm)',
};

const CardHeader: SlotComponent = ({ style, children, ...rest }) => (
    <div style={{ ...headerStyle, ...style }} {...rest}>{children}</div>
);
CardHeader[SLOT] = 'header';
CardHeader.displayName = 'Card.Header';

const CardBody: SlotComponent = ({ style, children, ...rest }) => (
    <div style={{ ...bodyStyle, ...style }} {...rest}>{children}</div>
);
CardBody[SLOT] = 'body';
CardBody.displayName = 'Card.Body';

const CardFooter: SlotComponent = ({ style, children, ...rest }) => (
    <div style={{ ...footerStyle, ...style }} {...rest}>{children}</div>
);
CardFooter[SLOT] = 'footer';
CardFooter.displayName = 'Card.Footer';

const CardRoot: React.FC<CardProps> & {
    Header: typeof CardHeader;
    Body: typeof CardBody;
    Footer: typeof CardFooter;
} = ({
    interactive = false,
    style,
    onMouseEnter,
    onMouseLeave,
    children,
    ...rest
}) => {
    const [hover, setHover] = useState(false);
    const compositional = hasAnySlot(children);

    const baseStyle: React.CSSProperties = {
        background: interactive && hover ? 'var(--color-bg-card-hover)' : 'var(--color-bg-card)',
        color: 'var(--color-text-primary)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: compositional ? 0 : '1rem 1.25rem',
        boxShadow: interactive && hover
            ? '0 8px 30px rgba(0, 0, 0, 0.3), 0 0 15px color-mix(in srgb, var(--color-brand) 10%, transparent)'
            : 'none',
        transition: 'background 120ms ease, box-shadow 120ms ease',
        overflow: 'hidden',
        ...style,
    };

    return (
        <div
            style={baseStyle}
            onMouseEnter={(e) => { if (interactive) setHover(true); onMouseEnter?.(e); }}
            onMouseLeave={(e) => { if (interactive) setHover(false); onMouseLeave?.(e); }}
            {...rest}
        >
            {children}
        </div>
    );
};

CardRoot.Header = CardHeader;
CardRoot.Body = CardBody;
CardRoot.Footer = CardFooter;

export const Card = CardRoot;

export default Card;
